import { useQuery } from '@tanstack/react-query';
import { jordanDate, ROUTES, type MessageRecord } from '@serb/shared';
import { api, errorText } from '../../api/client';
import { KEYS } from '../../api/keys';
import { SAMPLE_DATA } from '../../lib/sample';
import { useApprovals } from '../approvals/useApprovals';
import { blankShipment, SHIPMENTS, type Shipment } from '../shipments/sample';
import { useShipments } from '../shipments/useShipments';
import { buildConversations, buildEvents } from './build';
import { CONVERSATIONS, EVENTS, type Conversation } from './sample';

/**
 * What came in and went out, per shipment: the activity feed and the conversations. Read from the vault's message log;
 * the messages themselves stay in the mail app. Made-up ones only while sample data is on.
 */
/** A conversation together with its shipment */
export type Row = { conv: Conversation; ship: Shipment };

export function useCommunications() {
  const q = useQuery({
    queryKey: KEYS.messages,
    queryFn: () => api.get<MessageRecord[]>(ROUTES.api.messages),
    enabled: !SAMPLE_DATA,
  });
  const approvals = useApprovals().items;
  const { items: ships } = useShipments();

  const messages = q.data ?? [];
  const events = SAMPLE_DATA ? EVENTS : buildEvents(messages, approvals, jordanDate());
  const conversations = SAMPLE_DATA ? CONVERSATIONS : buildConversations(messages, events);
  /** each conversation together with its shipment (a blank one when the vault has no such shipment) */
  const rows = conversations.map((conv) => ({
    conv,
    ship:
      ships.find((s) => s.srb === conv.srb) ?? SHIPMENTS.find((s) => s.srb === conv.srb) ?? blankShipment(conv.srb, ''),
  }));
  return {
    events,
    conversations,
    rows,
    loading: !SAMPLE_DATA && q.isPending,
    error: q.isError ? errorText(q.error, 'The messages could not be loaded.') : '',
  };
}
